"use client";

import React, { createContext, useContext, useRef } from "react";
import { useScroll, useTransform, motion, MotionValue } from "framer-motion";
import { cn } from "../../lib/utils";

type TextOpacityEnum = "none" | "soft" | "medium";
type ViewTypeEnum = "word" | "letter";

type TextGradientScrollType = {
  text: string;
  type?: ViewTypeEnum;
  className?: string;
  textOpacity?: TextOpacityEnum;
};

type LetterType = {
  children: React.ReactNode | string;
  progress: MotionValue<number>;
  range: number[];
};

type WordType = {
  children: React.ReactNode;
  progress: MotionValue<number>;
  range: number[];
};

type CharType = {
  children: React.ReactNode;
  progress: MotionValue<number>;
  range: number[];
};

type TextGradientScrollContextType = {
  textOpacity?: TextOpacityEnum;
  type?: ViewTypeEnum;
};

const TextGradientScrollContext = createContext<TextGradientScrollContextType>(
  {}
);

function useGradientScroll() {
  const context = useContext(TextGradientScrollContext);
  return context;
}

function TextGradientScroll({
  text,
  className,
  type = "letter",
  textOpacity = "soft",
}: TextGradientScrollType) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"],
  });

  // Split by line breaks first, then by words
  const lines = text.split('\n');
  const allWords: string[] = [];
  const lineBreaks: number[] = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) lineBreaks.push(allWords.length);
    const lineWords = line.trim().split(" ").filter(word => word.length > 0);
    allWords.push(...lineWords);
  });

  return (
    <TextGradientScrollContext.Provider value={{ textOpacity, type }}>
      <div ref={ref} className={cn("tw-relative tw-m-0 tw-font-medium", className)}>
        {allWords.map((word, i) => {
          const start = i / allWords.length;
          const end = start + 1 / allWords.length;
          const isLineBreak = lineBreaks.includes(i);

          return (
            <React.Fragment key={i}>
              {isLineBreak && <br className="tw-block tw-mb-4" />}
              <span className="tw-inline-block tw-mr-2">
                {type === "word" ? (
                  <Word progress={scrollYProgress} range={[start, end]}>
                    {word}
                  </Word>
                ) : (
                  <Letter progress={scrollYProgress} range={[start, end]}>
                    {word}
                  </Letter>
                )}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </TextGradientScrollContext.Provider>
  );
}

export { TextGradientScroll };

const Word = ({ children, progress, range }: WordType) => {
  const opacity = useTransform(progress, range, [0, 1]);

  return (
    <span className="tw-relative">
      <span style={{ position: "absolute", opacity: 0.1 }}>{children}</span>
      <motion.span style={{ transition: "all .5s", opacity: opacity }}>
        {children}
      </motion.span>
    </span>
  );
};

const Letter = ({ children, progress, range }: LetterType) => {
  if (typeof children === "string") {
    const amount = range[1] - range[0];
    const step = amount / children.length;

    return (
      <span className="tw-relative">
        {children.split("").map((char: string, i: number) => {
          const start = range[0] + i * step;
          const end = range[0] + (i + 1) * step;
          return (
            <Char key={`c_${i}`} progress={progress} range={[start, end]}>
              {char}
            </Char>
          );
        })}
      </span>
    );
  }

  return null;
};

const Char = ({ children, progress, range }: CharType) => {
  const opacity = useTransform(progress, range, [0, 1]);
  const { textOpacity } = useGradientScroll();

  return (
    <span>
      <span
        className={cn("tw-absolute", {
          "tw-opacity-0": textOpacity == "none",
          "tw-opacity-10": textOpacity == "soft",
          "tw-opacity-30": textOpacity == "medium",
        })}
      >
        {children}
      </span>
      <motion.span
        style={{
          transition: "all .5s",
          opacity: opacity,
        }}
      >
        {children}
      </motion.span>
    </span>
  );
};